import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { ResponsiveLine } from '@nivo/line';
import * as reportService from '../services/reportService';

export function SprintReport(): React.ReactElement {
  const { token } = useAuth();
  const [sprints, setSprints] = useState<reportService.SprintOption[]>([]);
  const [selectedSprintId, setSelectedSprintId] = useState<string>('');
  const [reportData, setReportData] = useState<reportService.SprintReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [sprintsLoading, setSprintsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [burndownMode, setBurndownMode] = useState<'points' | 'items'>('points');

  // Load all sprints on mount
  useEffect(() => {
    async function loadSprints() {
      if (!token) return;
      try {
        setSprintsLoading(true);
        const data = await reportService.getAllSprints(token);
        setSprints(data);
        // Auto-select the first sprint if available
        if (data.length > 0) {
          setSelectedSprintId(data[0].id);
        }
      } catch (err: any) {
        console.error('Error loading sprints:', err);
        setError('Failed to load sprints');
      } finally {
        setSprintsLoading(false);
      }
    }
    loadSprints();
  }, [token]);

  // Load report when sprint is selected
  useEffect(() => {
    async function loadReport() {
      if (!token || !selectedSprintId) {
        setReportData(null);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const data = await reportService.getSprintReport(selectedSprintId, token);
        setReportData(data);
        // Default to points if available, otherwise items
        setBurndownMode(data.burndown_points.length > 0 ? 'points' : 'items');
      } catch (err: any) {
        console.error('Error loading sprint report:', err);
        setError('Failed to load sprint report');
        setReportData(null);
      } finally {
        setLoading(false);
      }
    }
    loadReport();
  }, [token, selectedSprintId]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'low':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStoryTypeBadgeClass = (storyType: string | null) => {
    switch (storyType) {
      case 'bug':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'story':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'task':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'spike':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'epic':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400';
      case 'sub-task':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getSprintStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'completed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'planning':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Render sprint-over-sprint comparison indicator
  const renderComparison = (current: number, previous: number | undefined, invertColor: boolean = false) => {
    if (previous === undefined || previous === null) return null;
    const diff = current - previous;
    const pctChange = previous === 0 ? (current > 0 ? 100 : 0) : Math.round((diff / previous) * 100);
    const isUp = diff > 0;
    const isNeutral = diff === 0;
    // invertColor: for "Carried Over", going down is good (green), up is bad (red)
    const colorClass = isNeutral
      ? 'text-gray-400 dark:text-gray-500'
      : (isUp !== invertColor)
        ? 'text-green-600 dark:text-green-400'
        : 'text-red-500 dark:text-red-400';

    return (
      <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Prev: {previous}</span>
          {!isNeutral && (
            <span className={`inline-flex items-center text-sm font-bold ${colorClass}`}>
              {isUp ? (
                <svg className="w-4 h-4 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
              ) : (
                <svg className="w-4 h-4 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              )}
              {Math.abs(pctChange)}%
            </span>
          )}
          {isNeutral && (
            <span className={`text-sm font-bold ${colorClass}`}>0%</span>
          )}
        </div>
      </div>
    );
  };

  // Prepare burndown chart data for nivo based on selected mode
  const burndownChartData = useMemo(() => {
    if (!reportData) return [];
    const source = burndownMode === 'points' ? reportData.burndown_points : reportData.burndown_items;
    if (!source || source.length === 0) return [];

    const actualData = source
      .filter(p => p.remaining !== null)
      .map(p => ({ x: p.date, y: p.remaining as number }));

    const idealData = source
      .map(p => ({ x: p.date, y: p.ideal }));

    const series: any[] = [
      { id: 'Ideal', data: idealData },
    ];

    if (actualData.length > 0) {
      series.push({ id: 'Actual', data: actualData });
    }

    return series;
  }, [reportData, burndownMode]);

  const hasPointsData = (reportData?.burndown_points?.length ?? 0) > 0;
  const hasItemsData = (reportData?.burndown_items?.length ?? 0) > 0;

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold text-white">End of Sprint Report</h1>
          <p className="text-blue-100 mt-1">View breakdown of completed vs carried-over items</p>
        </div>

        {/* Sprint Selector */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Sprint
          </label>
          {sprintsLoading ? (
            <div className="animate-pulse h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
          ) : sprints.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">No sprints found. Create a sprint to generate reports.</p>
          ) : (
            <select
              value={selectedSprintId}
              onChange={(e) => setSelectedSprintId(e.target.value)}
              className="w-full md:w-96 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Select a sprint...</option>
              {sprints.map((sprint) => (
                <option key={sprint.id} value={sprint.id}>
                  {sprint.project_code ? `${sprint.project_code} - ` : ''}{sprint.name}{sprint.team_name ? ` · ${sprint.team_name}` : ''} ({sprint.status})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        )}

        {/* Report Content */}
        {!loading && reportData && (
          <>
            {/* Sprint Info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                      {reportData.sprint.name}
                    </h2>
                    {reportData.sprint.team_name && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                        {reportData.sprint.team_name}
                      </span>
                    )}
                  </div>
                  {reportData.sprint.project_name && (
                    <p className="text-gray-500 dark:text-gray-400">
                      {reportData.sprint.project_name} ({reportData.sprint.project_code})
                    </p>
                  )}
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSprintStatusBadge(reportData.sprint.status)}`}>
                  {reportData.sprint.status}
                </span>
                <div className="ml-auto text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(reportData.sprint.start_date)} - {formatDate(reportData.sprint.end_date)}
                </div>
              </div>
              {reportData.sprint.goal && (
                <p className="mt-3 text-gray-600 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700 pt-3">
                  <span className="font-medium">Goal:</span> {reportData.sprint.goal}
                </p>
              )}
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Items</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {reportData.summary.total_items}
                </p>
                {renderComparison(reportData.summary.total_items, reportData.previous_summary?.total_items)}
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Completed</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {reportData.summary.completed_count}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {reportData.summary.completion_rate}% completion rate
                </p>
                {renderComparison(reportData.summary.completed_count, reportData.previous_summary?.completed_count)}
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Carried Over</p>
                <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                  {reportData.summary.carried_over_count}
                </p>
                {renderComparison(reportData.summary.carried_over_count, reportData.previous_summary?.carried_over_count, true)}
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Velocity</p>
                <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                  {reportData.summary.velocity}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  of {reportData.summary.planned_points} planned pts
                </p>
                {renderComparison(reportData.summary.velocity, reportData.previous_summary?.velocity)}
              </div>
            </div>

            {/* Burndown Chart */}
            {burndownChartData.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Sprint Burndown</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {burndownMode === 'items' ? 'Items' : 'Story points'} remaining over time
                    </p>
                  </div>
                  {hasPointsData && hasItemsData && (
                    <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
                      <button
                        onClick={() => setBurndownMode('points')}
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                          burndownMode === 'points'
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                      >
                        Points
                      </button>
                      <button
                        onClick={() => setBurndownMode('items')}
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                          burndownMode === 'items'
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                      >
                        Items
                      </button>
                    </div>
                  )}
                </div>
                <div className="px-6 py-4" style={{ height: 350 }}>
                  <ResponsiveLine
                    data={burndownChartData}
                    margin={{ top: 20, right: 30, bottom: 50, left: 50 }}
                    xScale={{ type: 'point' }}
                    yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false }}
                    axisBottom={{
                      tickRotation: -45,
                      format: (v: string) => {
                        const d = new Date(v + 'T00:00:00');
                        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      },
                      legendOffset: 40,
                      legendPosition: 'middle' as const,
                    }}
                    axisLeft={{
                      legend: burndownMode === 'items' ? 'Items' : 'Story Points',
                      legendOffset: -40,
                      legendPosition: 'middle' as const,
                    }}
                    colors={['#a5b4fc', '#4f46e5']}
                    lineWidth={2}
                    pointSize={6}
                    pointColor={{ from: 'color' }}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    enablePointLabel={false}
                    useMesh={true}
                    enableSlices="x"
                    sliceTooltip={({ slice }: any) => (
                      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          {new Date(slice.points[0].data.x + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                        {slice.points.map((point: any) => (
                          <div key={point.id} className="flex items-center gap-2 text-sm">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: point.serieColor }} />
                            <span className="text-gray-700 dark:text-gray-300">{point.serieId}:</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{point.data.y} {burndownMode === 'items' ? 'items' : 'pts'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    legends={[
                      {
                        anchor: 'top-right' as const,
                        direction: 'row' as const,
                        translateY: -20,
                        itemWidth: 80,
                        itemHeight: 20,
                        symbolSize: 10,
                        symbolShape: 'circle' as const,
                      },
                    ]}
                    theme={{
                      axis: {
                        ticks: { text: { fill: '#9ca3af', fontSize: 11 } },
                        legend: { text: { fill: '#9ca3af', fontSize: 12 } },
                      },
                      grid: { line: { stroke: '#e5e7eb', strokeWidth: 1 } },
                      legends: { text: { fill: '#6b7280', fontSize: 12 } },
                    }}
                    enableArea={false}
                    curve="monotoneX"
                  />
                </div>
              </div>
            )}

            {/* Completed Items Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                  Completed Items ({reportData.completed_items.length})
                </h3>
              </div>
              {reportData.completed_items.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  No items were completed in this sprint
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Priority
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Points
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Assignee
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Completed At
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {reportData.completed_items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {item.title}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {item.story_type && (
                              <span className={`px-2 py-1 text-xs rounded-full ${getStoryTypeBadgeClass(item.story_type)}`}>
                                {item.story_type}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadgeClass(item.priority)}`}>
                              {item.priority}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {item.story_points ?? '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {item.assigned_to_name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {formatDateTime(item.completed_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Carried Over Items Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <span className="w-3 h-3 bg-orange-500 rounded-full mr-2"></span>
                  Carried Over Items ({reportData.carried_over_items.length})
                </h3>
              </div>
              {reportData.carried_over_items.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  All items were completed - great job!
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Priority
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Points
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Assignee
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Current Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {reportData.carried_over_items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {item.title}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {item.story_type && (
                              <span className={`px-2 py-1 text-xs rounded-full ${getStoryTypeBadgeClass(item.story_type)}`}>
                                {item.story_type}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadgeClass(item.priority)}`}>
                              {item.priority}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {item.story_points ?? '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {item.assigned_to_name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {item.column_name || item.status}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Empty State */}
        {!loading && !reportData && selectedSprintId && !error && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No report available</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Select a sprint to view its report.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default SprintReport;
